import { PropertyValueSet } from "@genesys/property";
import { PropertyDefinitions, getAllPropertyUpdates } from "../tools";
import { queryUser } from "../queries";
import { Action } from "./actions";
import { SettingsOverrides, BinData } from "../tools";
import { State } from "./types";
import {
  getCaseOverrideUpdates,
  caseOverridesData
} from "../tools/moisture-load-rules/case-overrides-settings";
import * as SharedState from "../../shared-state";
import * as PropertiesSelector from "../../properties-selector";
import * as LoadTypesContent from "../main-content/load-types";
import * as OperationTimeGen2 from "../../operation-time-manager";
import * as GraphQLTypes from "../../graphql-types";
import * as ClimateSettingsEditor from "../components/climate-settings-editor";
import * as Main from "../main-content";
import * as KnownProperties from "../tools/known-properties";

export function getUpdatedProperties(
  newPropertiesSelectorState: PropertiesSelector.State,
  previousSelections: PropertyValueSet.PropertyValueSet,
  propertyDefinitions: PropertyDefinitions
) {
  const currentSelections = PropertiesSelector.getSelectedProperties(
    newPropertiesSelectorState!
  );
  return PropertyValueSet.merge(
    getAllPropertyUpdates(
      previousSelections,
      currentSelections,
      propertyDefinitions,
      true
    ),
    currentSelections
  );
}

export function getNewSystemOverrides(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet,
  currentSystemOverrides: SettingsOverrides,
  updatedValues: PropertyValueSet.PropertyValueSet
): SettingsOverrides {
  // Determine if all overrides should be cleaned up
  const cleanUpOverrides = caseOverridesData.every(
    ip =>
      ip.manualInputProperty === undefined ||
      (ip.manualInputProperty &&
        PropertyValueSet.hasProperty(
          ip.manualInputProperty,
          currentSelections
        ) &&
        PropertyValueSet.getInteger(
          ip.manualInputProperty,
          currentSelections
        ) === 0)
  );

  // Convert enum keys to a typed array for iteration
  const operatingCaseGroups = Object.keys(
    GraphQLTypes.OperatingCaseGroupEnum
  ) as ReadonlyArray<GraphQLTypes.OperatingCaseGroupEnum>;

  // Create a mutable copy of the overrides
  const newOverrides: Record<
    GraphQLTypes.OperatingCaseGroupEnum,
    PropertyValueSet.PropertyValueSet
  > = { ...currentSystemOverrides };

  // Process each operating case group
  for (const operatingCaseResultGroup of operatingCaseGroups) {
    if (cleanUpOverrides) {
      // Reset the overrides for this group if cleaning up
      newOverrides[operatingCaseResultGroup] = currentSelections;
    } else {
      const prevOverrideForResultGroup =
        currentSystemOverrides?.[operatingCaseResultGroup] || currentSelections;

      // Compute the updated overrides for this group
      const updatedOverride = getCaseOverrideUpdates(
        operatingCaseResultGroup,
        previousSelections,
        currentSelections,
        prevOverrideForResultGroup,
        updatedValues
      );

      // Merge the updated overrides with the previous ones
      newOverrides[operatingCaseResultGroup] = PropertyValueSet.merge(
        updatedOverride,
        prevOverrideForResultGroup
      );
    }
  }

  // Return the updated overrides as the immutable `SettingsOverrides`
  return newOverrides as SettingsOverrides;
}

export function createMoistureLoadInputSettingsForCalc(
  state: State,
  binDataInfo: BinData,
  // loadsSettings: ReadonlyArray<

  // >
  // loadsOperationTime: ReadonlyArray<{
  //   readonly sourceName: GraphQLTypes.MoistureHeatLoadTypeEnum;
  //   readonly operationTimeState: OperationTimeGen2.State;
  // }>,

  topLevelCaseOverrides: ReadonlyArray<{
    readonly operatingCaseGroup: GraphQLTypes.OperatingCaseGroupEnum;
    readonly moistureLoadSettings: string;
  }>
): Omit<GraphQLTypes.MoistureLoadInputType, "logAllSteps" | "showDevView"> {
  let binSettingsPvs = PropertyValueSet.Empty;
  binSettingsPvs = PropertyValueSet.setText(
    KnownProperties.binDataLocationId,
    binDataInfo.locationId,
    binSettingsPvs
  );
  binSettingsPvs = PropertyValueSet.setText(
    KnownProperties.climateCoolingDataType,
    binDataInfo.dataType,
    binSettingsPvs
  );
  binSettingsPvs = PropertyValueSet.setInteger(
    KnownProperties.binSize,
    binDataInfo.binSize,
    binSettingsPvs
  );

  const selectedLoads = LoadTypesContent.getCurrentSelectedLoads(
    state.mainContentState?.loadTypesContentState!
  ).flatMap(x => x);

  const moistureLoadSettings = PropertyValueSet.toString(
    PropertiesSelector.getSelectedProperties(state.propertiesSelectorState!)
  );

  const operatingCaseGroupEnum = Object.keys(
    GraphQLTypes.OperatingCaseGroupEnum
  ) as ReadonlyArray<GraphQLTypes.OperatingCaseGroupEnum>;

  return {
    moistureLoadSettings: moistureLoadSettings,
    // System operation time as stringified property value set.
    systemOperationTime: PropertyValueSet.toString(
      OperationTimeGen2.toPropertyValueSet(
        Main.getSystemAndBuildingConfigState(state.mainContentState!)!
          .operationTimeState!
      )
    ),

    // List of case specific settings.
    caseOverrides: topLevelCaseOverrides,
    binSettings: PropertyValueSet.toString(binSettingsPvs),
    loads: selectedLoads.map(load => ({
      id: load.id,
      operationTime: PropertyValueSet.toString(
        OperationTimeGen2.toPropertyValueSet(load.operationTimeState!)
      ),
      name: load.name,
      loadType: load.groupName,
      loadSettingsOverrides: !operatingCaseGroupEnum.some(x =>
        PropertyValueSet.isEmpty(load.caseOverrides[x])
      )
        ? operatingCaseGroupEnum.map(operatingCaseGroup => ({
            operatingCaseGroup: operatingCaseGroup,
            moistureLoadSettings: PropertyValueSet.toString(
              load.caseOverrides![operatingCaseGroup]
            )
          }))
        : [],

      settings: PropertyValueSet.toString(load.settings)
    })),

    climateSettings: PropertyValueSet.toString(
      ClimateSettingsEditor.getClimateSettings(
        state.climateSettingsEditorState!
      )!
    )
  };
}

export function fechUserQuery(
  sharedState: SharedState.State,
  moistureLoadNo: number,
  revisionNo: number
) {
  return sharedState.graphQL.queryUser<
    GraphQLTypes.MoistureLoadCalculationUserQuery,
    GraphQLTypes.MoistureLoadCalculationUserQueryVariables
  >(queryUser, {
    moistureLoadNo: moistureLoadNo,
    revisionNo: revisionNo
  });
}

export function fechUserQueryCmd(
  sharedState: SharedState.State,
  moistureLoadNo: number,
  revisionNo: number
) {
  return sharedState.graphQL.queryUserCmd<
    GraphQLTypes.MoistureLoadCalculationUserQuery,
    GraphQLTypes.MoistureLoadCalculationUserQueryVariables,
    Action
  >(
    queryUser,
    {
      moistureLoadNo: moistureLoadNo,
      revisionNo: revisionNo
    },
    Action.userQueryRecieved
  );
}
